import { slugify } from '@/utils/string';
import clsx from 'clsx';
import React from 'react';

const sizeClasses = {
  heading: 'py-2 px-2 text-sm sm:px-4 sm:text-base',
  compact: 'py-2 px-2 text-sm',
} as const;

const trackEmphasisClasses = {
  high: 'bg-cloud',
  low: 'bg-cloud',
} as const;

const activeSegmentEmphasisClasses = {
  high: 'bg-primary text-white',
  low: 'bg-white text-primary shadow-sm',
} as const;

const inactiveSegmentEmphasisClasses = {
  high: 'bg-white text-light',
  low: 'bg-cloud text-light',
} as const;

const defaultContainerClasses =
  'inline-flex items-center gap-1 p-1 w-full rounded-md transition-colors';

const defaultSegmentClasses =
  'text-center w-full rounded-md transition-colors disabled:cursor-not-allowed disabled:opacity-50';

export interface TabButtonOption {
  value: string;
  label: React.ReactNode;
}

export interface TabButtonProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'className' | 'onChange'> {
  options:
    | [TabButtonOption, TabButtonOption]
    | [TabButtonOption, TabButtonOption, TabButtonOption];
  value: string;
  onChange: (value: string) => void;
  label?: string;
  emphasis?: 'high' | 'low';
  size?: keyof typeof sizeClasses;
  disabled?: boolean;
  className?: string;
}

const TabButton = React.forwardRef<HTMLDivElement, TabButtonProps>(
  (
    {
      options,
      value,
      onChange,
      label,
      emphasis = 'low',
      size = 'compact',
      disabled = false,
      className = '',
      id,
      ...divProps
    },
    ref
  ) => {
    const groupId = id ?? (label ? `tabbutton-${slugify(label)}` : undefined);
    const labelId = label && groupId ? `${groupId}-label` : undefined;

    return (
      <div>
        {label && (
          <p id={labelId} className="mb-2 block text-sm text-dark">
            {label}
          </p>
        )}
        <div
          ref={ref}
          id={groupId}
          role="group"
          aria-labelledby={labelId}
          className={clsx(
            defaultContainerClasses,
            trackEmphasisClasses[emphasis],
            className
          )}
          {...divProps}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              disabled={disabled}
              onClick={() => onChange(option.value)}
              className={clsx(
                defaultSegmentClasses,
                sizeClasses[size],
                option.value === value
                  ? clsx(
                      'font-semibold',
                      activeSegmentEmphasisClasses[emphasis]
                    )
                  : inactiveSegmentEmphasisClasses[emphasis]
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    );
  }
);

TabButton.displayName = 'TabButton';

export default TabButton;
